import type { APIKey, Budget, Bucket, Connection, Connector, Credential, Device, GatewayDomain, GatewayKind, GatewayOutput, Issuer, Kind, Latest, Me, Operation, OperationRun, Presentation, QueryCode, QueryReference, Rule, RuleState, RunSummary, TextReading, TrafficBucket, TrafficSummary } from '@types'
import type { MarJsonValue } from '@nimling/components-nimtech'
import { readStream, EVENT_STREAM, type Step } from '@nimling/samna-auth-middleware'
import { SERVER_ENDPOINT, apiKey, signOut, standalone } from '@lib/store/session'

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message)
    this.name = 'ApiError'
  }
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

export async function api<T>(path: string, method: Method = 'GET', body?: unknown): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  const key = apiKey.value
  if (key) headers.Authorization = `Bearer ${key}`
  const res = await fetch(`${SERVER_ENDPOINT}${path}`, {
    method,
    credentials: 'include',
    headers,
    body: body === undefined ? undefined : JSON.stringify(body)
  })
  const text = await res.text()
  const parsed = text ? JSON.parse(text) : null
  if (res.status === 401) {
    if (standalone()) signOut()
    else window.location.href = `${SERVER_ENDPOINT}/auth/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`
    throw new ApiError(401, parsed?.error?.message ?? parsed?.error ?? 'unauthorized')
  }
  if (!res.ok) {
    throw new ApiError(res.status, parsed?.error?.message ?? parsed?.error ?? res.statusText)
  }
  return (parsed && typeof parsed === 'object' && 'data' in parsed ? parsed.data : parsed) as T
}

const qs = (params: Record<string, string | number | undefined | null>) => {
  const q = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') q.set(k, String(v))
  }
  const s = q.toString()
  return s ? `?${s}` : ''
}

export const getMe = () => api<Me>('/me')
export const getConnectors = () => api<Connector[]>('/connectors')
export const getConnector = (slug: string) => api<Connector>(`/connectors/${slug}`)
export const getKinds = () => api<Kind[]>('/kinds')
export const getBudgets = () => api<Budget[]>('/budgets')
export const postScriptCheck = (code: string) => api<{ ok: boolean; error?: string }>('/scripts/check', 'POST', { code })

export const getConnections = () => api<Connection[]>('/connections')
export const getConnection = (id: string) => api<Connection>(`/connections/${id}`)
export const postConnection = (body: { name: string; connector: string; config: Record<string, unknown> }) => api<Connection>('/connections', 'POST', body)
export const putConnection = (id: string, body: Record<string, unknown>) => api<Connection>(`/connections/${id}`, 'PUT', body)
export const deleteConnection = (id: string) => api<unknown>(`/connections/${id}`, 'DELETE')
export const postConnectionSecret = (id: string) => api<Connection>(`/connections/${id}/secret`, 'POST')
export const postConnectionDraft = (body: { connector: string; config: Record<string, unknown>; operation?: string; code?: string; input?: unknown }) => api<RunSummary>('/connections/draft', 'POST', body)
export const getConnectionOperations = (id: string) => api<Operation[]>(`/connections/${id}/operations`)
export const postConnectionOperation = (id: string, body: Record<string, unknown>) => api<Operation>(`/connections/${id}/operations`, 'POST', body)
export const postConnectionOperationCall = (id: string, name: string, input: MarJsonValue) => api<MarJsonValue>(`/connections/${id}/operations/${name}`, 'POST', input)

export const getOperations = (params: { connection_id?: string; direction?: string; trigger?: string; transport?: string } = {}) => api<Operation[]>(`/operations${qs(params)}`)
export const getOperation = (id: string) => api<Operation>(`/operations/${id}`)
export const putOperation = (id: string, body: Record<string, unknown>) => api<Operation>(`/operations/${id}`, 'PUT', body)
export const deleteOperation = (id: string) => api<unknown>(`/operations/${id}`, 'DELETE')
export const postOperationRun = (id: string, onStep: (step: Step) => void) => apiStream<RunSummary>(`/operations/${id}/run`, 'POST', undefined, onStep)
export const postOperationTest = (id: string, onStep: (step: Step) => void, input?: unknown) => apiStream<RunSummary>(`/operations/${id}/test`, 'POST', input === undefined ? {} : { input }, onStep)

export const getRuns = (params: { connection_id?: string; operation_id?: string; ok?: string; limit?: number } = {}) => api<OperationRun[]>(`/runs${qs(params)}`)

export const getDevices = (params: { connection_id?: string; kind?: string; search?: string } = {}) => api<Device[]>(`/devices${qs(params)}`)
export const patchDevice = (id: string, body: { name?: string; location_id?: string; bookable_id?: string }) => api<Device>(`/devices/${id}`, 'PATCH', body)

export interface SeriesParams {
  kind?: string
  connection_id?: string
  device_id?: string
  location_id?: string
  bookable_id?: string
  from?: string
  to?: string
  step?: string
  group?: string
}

export const getSeries = (params: SeriesParams) => api<Bucket[]>(`/series${qs({ ...params })}`)
export const getStats = (params: SeriesParams) => api<Bucket[]>(`/stats${qs({ ...params })}`)
export const getLatest = (params: { kind?: string; connection_id?: string } = {}) => api<Latest[]>(`/latest${qs(params)}`)
export const getTexts = (params: { kind?: string; connection_id?: string; device_id?: string; limit?: number } = {}) => api<TextReading[]>(`/texts${qs(params)}`)
export const postQuery = (body: QueryCode & { from?: string; to?: string }) => api<Record<string, MarJsonValue>[]>('/query', 'POST', body)
export const getQueryReference = () => api<QueryReference>('/query/reference')

export const getRules = () => api<Rule[]>('/rules')
export const postRule = (body: Partial<Rule>) => api<Rule>('/rules', 'POST', body)
export const patchRule = (id: string, body: Partial<Rule>) => api<Rule>(`/rules/${id}`, 'PATCH', body)
export const deleteRule = (id: string) => api<unknown>(`/rules/${id}`, 'DELETE')
export const getRuleState = (id: string) => api<RuleState[]>(`/rules/${id}/state`)
export const postRuleEvaluate = (id: string) => api<RuleState[]>(`/rules/${id}/evaluate`, 'POST')

export const getCredentials = () => api<Credential[]>('/credentials')
export const postCredential = (body: Partial<Credential>) => api<Credential>('/credentials', 'POST', body)
export const patchCredential = (id: string, body: Partial<Credential>) => api<Credential>(`/credentials/${id}`, 'PATCH', body)
export const deleteCredential = (id: string) => api<unknown>(`/credentials/${id}`, 'DELETE')
export const postCredentialScan = () => api<Credential[]>('/credentials/scan', 'POST')

export const getAPIKeys = () => api<APIKey[]>('/keys')
export const postAPIKey = (body: { name: string; access: string; expires_at?: string }) => api<APIKey>('/keys', 'POST', body)
export const deleteAPIKey = (id: string) => api<unknown>(`/keys/${id}`, 'DELETE')


export async function apiStream<T>(path: string, method: Method, body: unknown, onStep: (step: Step) => void): Promise<T> {
  const headers: Record<string, string> = { Accept: EVENT_STREAM }
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  const key = apiKey.value
  if (key) headers.Authorization = `Bearer ${key}`
  const res = await fetch(`${SERVER_ENDPOINT}${path}`, {
    method,
    credentials: 'include',
    headers,
    body: body === undefined ? undefined : JSON.stringify(body)
  })
  const out = await readStream<{ data?: T; error?: { message?: string } } | null>(res, onStep)
  if (out.status === 401) {
    if (standalone()) signOut()
    else window.location.href = `${SERVER_ENDPOINT}/auth/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`
    throw new ApiError(401, out.body?.error?.message ?? 'unauthorized')
  }
  if (out.status >= 300) throw new ApiError(out.status, out.body?.error?.message ?? res.statusText)
  return (out.body && typeof out.body === 'object' && 'data' in out.body ? out.body.data : out.body) as T
}

export const getPresentations = () => api<Presentation[]>('/presentations')
export const getConnectionPresentations = (id: string) => api<Presentation[]>(`/connections/${id}/presentations`)
export const postPresentation = (body: Record<string, unknown>) => api<Presentation>('/presentations', 'POST', body)
export const putPresentation = (id: string, body: Record<string, unknown>) => api<Presentation>(`/presentations/${id}`, 'PUT', body)
export const deletePresentation = (id: string) => api<unknown>(`/presentations/${id}`, 'DELETE')

export const getIssuers = () => api<Issuer[]>('/issuers')
export const postIssuer = (body: Record<string, unknown>) => api<Issuer>('/issuers', 'POST', body)
export const putIssuer = (id: string, body: Record<string, unknown>) => api<Issuer>(`/issuers/${id}`, 'PUT', body)
export const deleteIssuer = (id: string) => api<unknown>(`/issuers/${id}`, 'DELETE')
export const getGatewayDomains = () => api<GatewayDomain[]>('/gateway/domains')
export const postGatewayDomain = (body: Record<string, unknown>) => api<GatewayDomain>('/gateway/domains', 'POST', body)
export const putGatewayDomain = (id: string, body: Record<string, unknown>) => api<GatewayDomain>(`/gateway/domains/${id}`, 'PUT', body)
export const deleteGatewayDomain = (id: string) => api<unknown>(`/gateway/domains/${id}`, 'DELETE')
export const getGateway = (kind: GatewayKind, params: Record<string, string | undefined>) => api<GatewayOutput>(`/gateway/${kind}${qs(params)}`)

export const getTraffic = (params: { connection_id?: string; since?: string } = {}) => api<TrafficBucket[]>(`/traffic${qs(params)}`)
export const getTrafficSummary = (params: { since?: string } = {}) => api<TrafficSummary[]>(`/traffic/summary${qs(params)}`)
